import { Locale } from "@definitions";
import { queryClient } from "src/api";
import { ApiError, initRequest, openRequest } from "src/api/endpoints";
import { ArticleItem, InitResponse, OpenResponse } from "src/api/schemas";
import { SessionData, SettingsFromQuery } from "src/core";
import type { DataElement } from "src/core/data-storage";

type RequestType = "init" | "open" | "save" | "email" | "pdf";
export type TypedApiError = ApiError & { requestType: RequestType };

type ApiNotificationStatus = "success" | "error";
export type ApiNotification = {
	status: ApiNotificationStatus;
	requestType: RequestType;
	name: string;
	message: string;
};

export type ApiExportData = {
	designId: string;
	name: string;
	items: ArticleItem[];
};
type DesignIdentity = {
	designId: string;
	name: string;
};

export type ArticleNumbersColorAndQuantity = {
	articleNumber: string;
	quantity: number;
	color: string;
};

export type Article = {
	ean: string | null;
	priceWithVat: number;
	priceWithoutVat: number;
	vat: number;
	quantity: number;
	name: string;
	sku: string;
	color: string;
};
export type ApiArticles = {
	[sku: string]: Omit<Article, "quantity" | "color"> & {
		packSize: number;
	};
};

type ApiDataManagerProps = {
	settingsFromQuery: SettingsFromQuery;
	getSerializedConfiguration: () => SessionData;
	generateSnapshot: () => Promise<string>;
	setFromStoredConfiguration: (configuration: SessionData) => void;
	designIdentityStorage: DataElement;
};

export class ApiDataManager {
	private _isLoading: boolean = false;
	private _apiData: InitResponse | undefined;
	private _apiNotification: ApiNotification | undefined;
	private _apiArticles: ApiArticles = {};
	private _currentArticles: Article[] = [];
	private _guid: SettingsFromQuery["guid"];
	private _pricelistId: SettingsFromQuery["pricelistid"];
	private _vatOverride: SettingsFromQuery["vat"];
	private _language: SettingsFromQuery["lan"];
	private _countryCode: SettingsFromQuery["countrycode"];
	private _designId: string = "";
	private _designName: string = "My Grythyttan Design";
	private _designIdentityStorage: DataElement;
	private _pendingConfigurationChange?: () => void;
	private _onStateUpdate: () => void;
	public getSerializedConfiguration: () => SessionData;
	public setFromStoredConfiguration: (configuration: SessionData) => void;
	public generateSnapshot: () => Promise<string>;

	constructor({
		settingsFromQuery,
		getSerializedConfiguration,
		setFromStoredConfiguration,
		generateSnapshot,
		designIdentityStorage,
	}: ApiDataManagerProps) {
		const { guid, pricelistid, vat, lan, countrycode } = settingsFromQuery;
		this._guid = guid;
		this._pricelistId = pricelistid;
		this._vatOverride = vat;
		this._language = lan;
		this._countryCode = countrycode;
		this.getSerializedConfiguration = getSerializedConfiguration;
		this.setFromStoredConfiguration = setFromStoredConfiguration;
		this.generateSnapshot = generateSnapshot;
		this._designIdentityStorage = designIdentityStorage;
		this._onStateUpdate = () => {
			//
		};
	}

	get guid(): string {
		return this._guid;
	}
	get pricelistId(): SettingsFromQuery["pricelistid"] {
		return this._pricelistId;
	}
	get vatOverride(): SettingsFromQuery["vat"] {
		return this._vatOverride;
	}
	get isLoading(): boolean {
		return this._isLoading;
	}
	get apiNotification(): ApiNotification | undefined {
		return this._apiNotification;
	}

	get currency(): string {
		return this._apiData?.data.currency ?? "";
	}

	get vat(): string {
		const vat = this._apiData?.data.vat;
		if (vat === undefined) {
			return "";
		}

		return `${parseFloat(((vat - 1) * 100).toFixed(10))}%`;
	}

	get countryCode(): SettingsFromQuery["countrycode"] {
		return this._countryCode;
	}

	get designId(): string {
		return this._designId;
	}

	get name(): string {
		return this._designName;
	}

	get articles(): Article[] {
		return this._currentArticles;
	}

	get apiArticles(): ApiArticles {
		return this._apiArticles;
	}

	/** Dev-only: overwrite articles for stock editor testing. */
	_devSetApiArticles(value: ApiArticles) {
		this._apiArticles = value;
	}

	get exportData(): ApiExportData {
		return this._getExportData();
	}

	set language(locale: Locale) {
		this._language = locale;
	}

	public addApiNotification(apiNotification: ApiNotification | undefined) {
		this._apiNotification = apiNotification;
	}

	public setDesignId(designId: string) {
		this._designId = designId;
		this._storeDesignIdentity();
	}

	public setDesignName(name: string) {
		this._designName = name;
		this._storeDesignIdentity();
	}

	/**
	 * Restores the design ID and name stored by an earlier page load, or, when
	 * `restore` is false, replaces them with the current ones. The stored
	 * identity belongs to the stored configuration, so pass true only when that
	 * configuration was restored.
	 */
	public initDesignIdentity(restore: boolean) {
		const stored = restore ? this._designIdentityStorage.get() : null;
		if (stored) {
			try {
				const { designId, name } = JSON.parse(
					stored,
				) as Partial<DesignIdentity>;
				if (typeof designId === "string") {
					this._designId = designId;
				}
				if (typeof name === "string") {
					this._designName = name;
				}
			} catch {
				console.warn(
					"Failed decoding the stored design identity, continuing without restoring it.",
				);
			}
		}
		this._storeDesignIdentity();
	}

	public setIsLoading(loading: boolean) {
		this._isLoading = loading;
		if (!loading && this._pendingConfigurationChange) {
			const pendingConfigurationChange = this._pendingConfigurationChange;
			this._pendingConfigurationChange = undefined;
			pendingConfigurationChange();
		}
		this._onStateUpdate();
	}

	public setOnStateChangeListener(listener: () => void) {
		this._onStateUpdate = listener;
	}

	public async initApi(): Promise<boolean> {
		const requestMethod = async () => {
			let response: InitResponse | undefined;
			try {
				response = await queryClient.fetchQuery<
					unknown,
					ApiError,
					InitResponse,
					(string | null)[]
				>({
					queryKey: [
						"init",
						this._guid,
						this._pricelistId,
						this._vatOverride,
						this._language,
						this._countryCode,
					],
					queryFn: () =>
						initRequest(
							this._guid,
							this._pricelistId,
							this._vatOverride,
							this._language,
							this._countryCode,
						),
				});
			} catch (error) {
				this._handleError(error, "init");
				return false;
			}

			this._apiData = response;
			this._mapArticlesBySku();

			return true;
		};

		return this._performRequest(requestMethod);
	}

	public async openDesign(designId: string): Promise<boolean> {
		const requestMethod = async () => {
			let response: OpenResponse | undefined;
			try {
				response = await queryClient.fetchQuery<
					unknown,
					ApiError,
					OpenResponse,
					(string | null)[]
				>({
					queryKey: ["open", this._guid, this._pricelistId, designId],
					queryFn: () => openRequest(this._guid, this._pricelistId, designId),
				});
			} catch (error) {
				this._handleError(error, "open");
				return false;
			}

			const { configuration, slug, name } = response.data;
			this.setFromStoredConfiguration(configuration);
			this.setDesignId(slug);
			this.setDesignName(name);
			return true;
		};

		return await this._performRequest(requestMethod);
	}

	public onConfigurationChanged(
		articleNumbersAndQuantity: ArticleNumbersColorAndQuantity[],
		onArticlesUpdatedCallback: (exportData: ApiExportData) => void,
	) {
		// Commit a NEW array (never mutate `_currentArticles` in place): React
		// consumers memoize on the array identity (e.g. useCartPricing's total),
		// so an in-place update renders as stale data. This matters on the
		// deferred path below — a stored-config load applies synchronously while
		// `_isLoading` is true, and the flush in `setIsLoading(false)` is the only
		// article update that render will ever see.
		const updateCurrentArticles = () => {
			const articles: Article[] = [];
			for (const {
				articleNumber,
				quantity,
				color,
			} of articleNumbersAndQuantity) {
				const apiArticle = this._apiArticles[articleNumber];
				if (!apiArticle) {
					console.warn(
						`Article number: ${articleNumber} not found in this._articlesData`,
					);
					continue;
				}
				articles.push({
					...apiArticle,
					quantity,
					color,
				});
			}
			this._currentArticles = articles;
			onArticlesUpdatedCallback(this._getExportData());
		};

		if (this._isLoading) {
			this._pendingConfigurationChange = () => {
				updateCurrentArticles();
			};
		} else {
			updateCurrentArticles();
		}
	}

	private _getExportData(): ApiExportData {
		return {
			designId: this._designId,
			items: this._currentArticles.map((article) => ({
				article: article.sku,
				ean: article.ean,
				quantity: article.quantity,
			})),
			name: this._designName,
		};
	}

	private _storeDesignIdentity() {
		const identity: DesignIdentity = {
			designId: this._designId,
			name: this._designName,
		};
		this._designIdentityStorage.set(JSON.stringify(identity));
	}

	private _mapArticlesBySku() {
		if (!this._apiData) {
			return;
		}
		this._apiArticles = this._apiData.data.products.reduce((acc, cur) => {
			const { ean, name, packSize, priceExVat, priceIncVat, sku, vatRate } =
				cur;
			acc[sku] = {
				ean,
				priceWithVat: priceIncVat,
				priceWithoutVat: priceExVat,
				vat: vatRate,
				packSize: Number(packSize),
				name,
				sku,
			};
			return acc;
		}, {} as ApiArticles);
	}

	private async _performRequest(requestMethod: () => Promise<boolean>) {
		this.setIsLoading(true);
		const isSuccess = await requestMethod();
		this.setIsLoading(false);

		return isSuccess;
	}

	private _handleError(error: unknown, requestType: RequestType) {
		const apiError = error as ApiError;
		this._apiNotification = {
			status: "error",
			requestType,
			message: apiError.message,
			name: apiError.name,
		};

		console.error("-- API Error --\n", apiError);
	}
}
